import React, { MouseEvent } from 'react';

import { SelectedShipment } from 'models/SelectRemove';
import type { Shipment } from 'models/shipments/Shipment';
import { compareShipments, matchEnquiriesToShipment } from 'utils/shipment.utils';

import type { ShipmentListItemConfig, ShipmentProp } from './ShipmentListItem/ShipmentListItem';
import ShipmentListItem from './ShipmentListItem/ShipmentListItem';
import { SHIPMENT_LIST_TEST_ID } from './ShipmentList.test-ids';

import styles from './ShipmentList.module.scss';
export interface Enquiry {
  articleId: string;
  cases: string[];
}

interface ShipmentListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of shipment information to display on the list */
  shipments: (Shipment | ShipmentProp)[];
  /** Apply milestone sort on the list */
  applySort?: boolean;
  /** A callback function to handle clicks on a shipment on the list */
  onItemClick?: (e?: MouseEvent<HTMLAnchorElement>, index?: number, shipment?: Shipment | ShipmentProp) => void;
  editMode?: boolean;
  selectedShipments?: SelectedShipment[];
  onItemSelectChange?: (event: React.ChangeEvent<HTMLInputElement>, shipment: Shipment | ShipmentProp) => void;
  shipmentEnquiries?: ShipmentEnquiries;
  config?: ShipmentListItemConfig;
}

export interface ShipmentEnquiries {
  /** Array of enquiries that should be displayed on the list for matching articles */
  enquiries: Enquiry[];
  /** A callback function to handle clicks on the Enquiry raised link */
  onEnquiryClick?: (e?: MouseEvent<HTMLAnchorElement>, caseIds?: string[], shipment?: Shipment | ShipmentProp) => void;
  /** endpoint for redirecting user to help and support on enquiry click */
  helpAndSupportBaseUrl: string;
}

/** Displays a list of shipments and their most relevant information, with each item linking to the detail page for that shipment.  */
const ShipmentList: React.FC<ShipmentListProps> = ({
  shipments,
  shipmentEnquiries,
  applySort = true,
  onItemClick,
  editMode = false,
  selectedShipments = [],
  onItemSelectChange,
  config,
  ...props
}) => {
  if (!shipments.length) return null;

  const shipmentList = applySort ? [...shipments].sort(compareShipments) : shipments;

  return (
    <div className={styles.shipmentList} data-testid={SHIPMENT_LIST_TEST_ID} {...props}>
      {shipmentList.map((shipment, index) => {
        /** If shipmentEnquiries prop has not been passed, then we set to undefined so that the column is hidden
         */
        const shipmentEnquiry = shipmentEnquiries && {
          enquiries: matchEnquiriesToShipment(shipmentEnquiries.enquiries, shipment),
          onEnquiryClick: shipmentEnquiries.onEnquiryClick,
          helpAndSupportBaseUrl: shipmentEnquiries.helpAndSupportBaseUrl,
        };

        let isSelected = false;
        const articleId = shipment?.articles?.length && shipment.articles[0].articleId;

        if (articleId && selectedShipments.length) {
          isSelected = !!selectedShipments.find((selected) => articleId === selected.trackingId && selected.checked);
        }

        return (
          <ShipmentListItem
            shipment={shipment}
            key={shipment.consignmentId}
            config={config}
            onClick={onItemClick && ((e) => onItemClick(e, index, shipment))}
            isSelected={isSelected}
            editMode={editMode}
            onItemSelectChange={onItemSelectChange}
            shipmentEnquiry={shipmentEnquiry}
          />
        );
      })}
    </div>
  );
};

export default ShipmentList;
